import User from '@/models/user';
import { UserSettings, UserProps } from '@/interfaces/models/user';
import userApi from '@/api/user';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '@/store';
import router from '@/router';

interface UserState {
  user: User | null,
  backUrl: string,
  isCreatePinCodeAfterSignIn: boolean
}

const namespaced: boolean = true;

const state: UserState = {
  user: null,
  backUrl: '/',
  isCreatePinCodeAfterSignIn: false
};

const getters: GetterTree<UserState, RootState> = {
  kitchenSettings(state) {
    return state.user && state.user.settings.kitchen;
  }
};

const actions: ActionTree<UserState, RootState> = {
  async login({ commit }: ActionContext<UserState, RootState>, credentials: any) {
    return userApi.login(credentials)
      .then(({ user, token, placeId }: { user: User, token: string, placeId: string }) => {
        commit('setUser', user);

        window.localStorage.setItem('accessToken', token);
        window.localStorage.setItem('placeId', placeId);

        return user;
      })
      .catch(console.log);
  },
  async logout({ dispatch }: ActionContext<UserState, RootState>) {
    await dispatch('forceResetStore', null, { root: true });
  },
  async removeStaff({ state }: ActionContext<UserState, RootState>, staffId: string) {
    if (state.user && state.user.id === staffId) {
      return router.push({
        name: 'logout',
      })
    }
  },
  async editUserSettings({ commit }: ActionContext<UserState, RootState>, editedSettings: any) {
    userApi.editUserSettings(editedSettings);

    return commit('setUserSettings', editedSettings);
  },
  async createStaffPinCode({ dispatch, state }: ActionContext<UserState, RootState>, pinCode: string) {
    if (state.user !== null) {
      await userApi.createPinCode(pinCode);

      return dispatch(
        'socketCreateStaffPinCode',
        { id: state.user.id, pinCode: pinCode },
        { root: true }
        );
    }
  }
};

const mutations: MutationTree<UserState> = {
  setUser(state, user: User) {
    state.user = user;
  },
  removeUser(state) {
    state.user = null;
  },
  editStaff(state, staffProps: UserProps) {
    if (null !== state.user && state.user.id === staffProps.id) {
      state.user.mergeWithNewFields(staffProps);
    }
  },
  setAuthBack(state, path: string) {
    state.backUrl = path;
  },
  makeStaffWithPinCode(state, { id, pinCode }: any) {
    if (state.user !== null && state.user.id === id) {
      state.user.isHavePinCode = true;
      state.user.pinCode = pinCode;
    }
  },
  updateAvailableSections(state, { staffId, updatedFields }: any) {
    if (state.user && state.user.id === staffId) {
      state.user.availableSections = { ...updatedFields.availableSections };
      state.user.isAdmin = updatedFields.isAdmin;
    }
  },
  setUserSettings(state, editedSettings: UserSettings) {
    if (state.user) {
      for (let key in editedSettings) {
        if (editedSettings.hasOwnProperty(key)) {
          state.user.settings[key] = {
            ...state.user.settings[key],
            ...editedSettings[key],
          };
        }
      }
    }
  },
  setIsCreatePinCodeAfterSignIn(state, isCreatePinCodeAfterSignIn: boolean) {
    state.isCreatePinCodeAfterSignIn = isCreatePinCodeAfterSignIn;
  }
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
